import { apiRequest, apiRequestVoid, API_BASE } from '@/lib/api-client';
import { toApiError } from '@/lib/api-errors';
import {
  getCandidateAccessToken,
  getCandidateRefreshToken,
  setCandidateSession,
} from '@/lib/session';

function authHeaders(): Record<string, string> {
  const token = getCandidateAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

/** Attempt a silent token refresh via httpOnly cookie. Returns the new access token or null. */
async function tryRefresh(): Promise<string | null> {
  try {
    const refreshToken = getCandidateRefreshToken();
    // Refresh token is in httpOnly cookie — sent automatically via credentials: 'include'
    const res = await fetch(`${API_BASE}/api/auth/refresh/candidate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
    });
    if (!res.ok) return null;
    const data = (await res.json()) as { accessToken: string; refreshToken?: string };
    setCandidateSession({ accessToken: data.accessToken, refreshToken: data.refreshToken });
    return data.accessToken;
  } catch {
    return null;
  }
}

/**
 * Authenticated fetch wrapper. On a 401 it attempts one silent token refresh
 * and retries the request. If the refresh also fails the tokens are cleared and
 * null is returned (the AuthContext's session-restore logic will then redirect
 * to /login on the next render cycle).
 */
async function apiFetch<T>(
  url: string,
  options?: RequestInit,
): Promise<T | null> {
  const doRequest = (opts?: RequestInit) =>
    fetch(`${API_BASE}${url.startsWith('/') ? url : `/${url}`}`, opts).catch(
      () => null,
    );

  let res = await doRequest(options);
  if (!res) return null;

  if (res.status === 401) {
    const newToken = await tryRefresh();
    if (!newToken) {
      // Refresh failed — clear in-memory token so AuthContext redirects to /login
      setCandidateSession({ accessToken: '' });
      return null;
    }
    // Retry original request with the fresh token injected into Authorization header
    const retryOptions: RequestInit = {
      ...options,
      headers: {
        ...(options?.headers ?? {}),
        Authorization: `Bearer ${newToken}`,
      },
    };
    res = (await doRequest(retryOptions)) ?? res;
    if (!res) return null;
  }

  if (!res.ok) return null;
  return res.json() as Promise<T>;
}

export interface PortalPublicState {
  isPortalOpen: boolean;
  maintenanceMode: boolean;
  openDate: string | null;
  closeDate: string | null;
  messageBeforeActivation: string | null;
  messageAfterDeactivation: string | null;
}

export interface CandidatePortalAccess {
  applicationsOpen: boolean;
  canUseApplicantActions: boolean;
  recruitmentCycleId: string | null;
  recruitmentCycleName: string | null;
  overrideExpiresAt: string | null;
  /**
   * 'assigned_only' — this candidate only sees roles an admin assigned them, and those roles
   * carry no 1st/2nd choice. 'inherit' — the normal eligibility-derived pool.
   */
  jobScope?: 'inherit' | 'assigned_only';
}

export interface PublicJob {
  id: number;
  guid: string;
  refNo: string;
  jobName: string;
  department: string | null;
  departmentName: string | null;
  sbu: string | null;
  sbuName: string | null;
  abu: string | null;
  abuName: string | null;
  jobLevel: string | null;
  jobLocation: string | null;
  noRequired: number | null;
  yearsOfExperience: string | null;
  description: string | null;
  jobCategoryId: number;
  jobCategoryName: string | null;
  maxApplicationsPerStaff: number;
  skillPoolId: number;
  skillPoolName: string | null;
  recruitmentCycleId: string | null;
}

export interface PublicJobDetail extends PublicJob {
  objectives: string[];
  responsibilities: string[];
  requiredQualifications: string[];
  requiredCompetencies: string[];
  jobSpecifications: string[];
  reportsTo: string | null;
  directlySupervises: string[];
  postNyscExperienceYears: number | null;
  maxAge: number | null;
  organizationCompetencies: string[];
  gradeCompetencies: string[];
  internalInterfaces: string[];
  externalInterfaces: string[];
  kpis: string[];
}

export interface MyApplication {
  id: number;
  guid: string;
  jobRoleId: number;
  jobRoleGuid: string | null;
  jobRoleName: string | null;
  jobChoice: number;
  /** 'assigned' — an admin directed the candidate to this role; no job choice, exempt from the cap. */
  source?: 'self' | 'assigned';
  submitted: boolean;
  submittedAt: string | null;
  canRecall?: boolean;
  createdAt: string;
}

/**
 * How many of the candidate's submissions consume their own application quota.
 *
 * Mirrors the server, which counts `submitted AND source = 'self'`: an admin-directed
 * application is exempt from the cap because the admin already bounded it by choosing which
 * roles to assign. Counting every submission here would block a candidate from a legitimate
 * self-service application once a directed grant is widened or revoked.
 *
 * A missing `source` counts as self-service, matching the column's default and how the server
 * treats rows that predate it. That can only over-count (block one step early), never
 * under-count — and the server remains the real gate.
 *
 * Use this for quota decisions only. Lists of "your submitted applications" must keep showing
 * every submission, directed ones included.
 */
export function countQuotaSubmissions(
  applications: Pick<MyApplication, 'submitted' | 'source'>[],
): number {
  return applications.filter((a) => a.submitted && a.source !== 'assigned')
    .length;
}

/** Mirrors API `CandidatePortalUserDto` — login / OAuth / dev impersonate `user` payload. */
export interface CandidatePortalUser {
  id: string;
  guid: string;
  staffId: string | null;
  applicantRefNo?: string | null;
  emailAddress: string | null;
  surname: string | null;
  firstName: string | null;
  middleName: string | null;
  gender: 'M' | 'F' | null;
  dateOfBirth: string | null;
  grade: string | null;
  designation: string | null;
  dateOfEmployment: string | null;
  nrd: string | null;
  yearsOfService: number | null;
  dateOfLastPromotion: string | null;
  phoneExt: string | null;
  gsmNumber: string | null;
  officeLabel: string | null;
  departmentCode: string | null;
  department: string | null;
  divisionCode: string | null;
  division: string | null;
  directorateCode: string | null;
  directorate: string | null;
  locationCode: string | null;
  location: string | null;
  skillPoolId: number | null;
  performanceTracking: string | null;
  verifiedQualificationWithDate: string | null;
  rank: number | null;
  eligibleForM3: boolean | null;
  eligibleForM4: boolean | null;
  eligibleForM5: boolean | null;
  eligibleForM6: boolean | null;
  m3EligibilityReason: string | null;
  m4EligibilityReason: string | null;
  m5EligibilityReason: string | null;
  m6EligibilityReason: string | null;
  eligibleGrades: string[];
  undertakingAcceptedAt: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface CandidateLoginResult {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  staffId: string | null;
  eligibleGrades: string[];
  user: CandidatePortalUser;
}

export async function getPortalState(): Promise<PortalPublicState | null> {
  try {
    return await apiRequest<PortalPublicState>(
      '/api/portal/public-state',
      undefined,
      { retryOn401: false },
    );
  } catch {
    return null;
  }
}

export interface SupportContacts {
  emails: string[];
  phones: string[];
}

export async function getSupportContacts(): Promise<SupportContacts> {
  try {
    const result = await apiRequest<SupportContacts>(
      '/api/portal/support-contacts',
      undefined,
      { retryOn401: false },
    );
    return result ?? { emails: [], phones: [] };
  } catch {
    return { emails: [], phones: [] };
  }
}

export async function getPublicJobs(grades: string[]): Promise<PublicJob[]> {
  const q = grades.length > 0 ? `?grades=${grades.join(',')}` : '';
  try {
    const result = await apiRequest<PublicJob[]>(`/api/jobs/public${q}`);
    return result ?? [];
  } catch {
    return [];
  }
}

export async function getPublicJob(
  id: string,
): Promise<PublicJobDetail | null> {
  // try {
  return await apiRequest<PublicJobDetail>(`/api/jobs/public/${id}`);
  // } catch {
  //   return null;
  // }
}

/** DEV ONLY — login as a candidate by staff ID with no password check */
export async function devImpersonate(
  staffId: string,
): Promise<CandidateLoginResult> {
  const res = await fetch(`${API_BASE}/api/auth/dev-impersonate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId }),
  });
  if (!res.ok) {
    throw await toApiError(res);
  }
  return res.json();
}

export async function candidateLogin(
  staffId: string,
  password: string,
): Promise<CandidateLoginResult> {
  const res = await fetch(`${API_BASE}/api/auth/candidate`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ staffId, password }),
  });
  if (!res.ok) {
    throw await toApiError(res);
  }
  return res.json();
}

export async function getMyApplications(): Promise<MyApplication[]> {
  try {
    const result = await apiRequest<MyApplication[] | null>(
      '/api/candidate/applications',
    );
    return result ?? [];
  } catch {
    return [];
  }
}

export async function getCandidatePortalAccess(): Promise<CandidatePortalAccess | null> {
  try {
    return await apiRequest<CandidatePortalAccess>('/api/candidate/applications/portal-access');
  } catch {
    return null;
  }
}

export async function createApplication(body: {
  jobRoleId: number;
  jobChoice?: number;
}): Promise<{
  id: number;
  guid: string;
  jobRoleId: number;
  jobRoleGuid: string | null;
}> {
  return apiRequest<{
    id: number;
    guid: string;
    jobRoleId: number;
    jobRoleGuid: string | null;
  }>('/api/candidate/applications', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export async function submitApplication(id: string): Promise<void> {
  await apiRequestVoid(`/api/candidate/applications/${id}/submit`, {
    method: 'POST',
  });
}

export async function recallApplication(id: string): Promise<void> {
  await apiRequestVoid(`/api/candidate/applications/${id}/recall`, {
    method: 'POST',
  });
}

export async function getApplication(
  id: string,
): Promise<ApplicationDetail | null> {
  try {
    return await apiRequest<ApplicationDetail>(
      `/api/candidate/applications/${id}`,
    );
  } catch {
    return null;
  }
}

/**
 * The job role behind one of my applications.
 *
 * Separate from `getPublicJob` on purpose: that endpoint requires the application period to
 * still be open, so it stops answering once the window closes. This one is authorised by
 * ownership of the application, which is what keeps the job readable alongside a submitted
 * summary after the portal has closed. Errors are surfaced, not swallowed — the dialog needs
 * to tell the candidate when the detail could not be loaded.
 */
export async function getApplicationJob(
  applicationId: string,
): Promise<PublicJobDetail | null> {
  return await apiRequest<PublicJobDetail>(
    `/api/candidate/applications/${applicationId}/job`,
  );
}

export async function updateApplication(
  id: string,
  body: Partial<ApplicationUpdate>,
): Promise<void> {
  await apiRequestVoid(`/api/candidate/applications/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

export type CopyApplicationSection =
  | 'employment'
  | 'commendations'
  | 'qualifications'
  | 'why'
  | 'yearsOfExperience';

export async function copyApplicationSections(
  targetAppId: string,
  sourceAppId: string,
  body: {
    sections: CopyApplicationSection[];
    mode?: 'append' | 'replace';
  },
): Promise<{ copied: Record<string, number | boolean>; note: string }> {
  return apiRequest<{ copied: Record<string, number | boolean>; note: string }>(
    `/api/candidate/applications/${targetAppId}/copy-from/${sourceAppId}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function saveWhy(appId: string, reason: string): Promise<void> {
  await apiRequestVoid(`/api/candidate/applications/${appId}/why`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
}

export async function addEmployment(
  appId: string,
  body: EmploymentBody,
): Promise<EmploymentRecord> {
  return apiRequest<EmploymentRecord>(
    `/api/candidate/applications/${appId}/employment`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function updateEmployment(
  appId: string,
  empId: number,
  body: EmploymentBody,
): Promise<EmploymentRecord> {
  return apiRequest<EmploymentRecord>(
    `/api/candidate/applications/${appId}/employment/${empId}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteEmployment(
  appId: string,
  empId: number,
): Promise<void> {
  await apiRequestVoid(
    `/api/candidate/applications/${appId}/employment/${empId}`,
    {
      method: 'DELETE',
    },
  );
}

export async function addQualification(
  appId: string,
  body: QualificationBody,
): Promise<QualificationRecord> {
  return apiRequest<QualificationRecord>(
    `/api/candidate/applications/${appId}/qualifications`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteQualification(
  appId: string,
  qualId: number,
): Promise<void> {
  await apiRequestVoid(
    `/api/candidate/applications/${appId}/qualifications/${qualId}`,
    { method: 'DELETE' },
  );
}

export async function addCertification(
  appId: string,
  body: CertificationBody,
): Promise<CertificationRecord> {
  return apiRequest<CertificationRecord>(
    `/api/candidate/applications/${appId}/certifications`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteCertification(
  appId: string,
  certId: number,
): Promise<void> {
  await apiRequestVoid(
    `/api/candidate/applications/${appId}/certifications/${certId}`,
    { method: 'DELETE' },
  );
}

export async function addCommendation(
  appId: string,
  body: CommendationBody,
): Promise<CommendationRecord> {
  return apiRequest<CommendationRecord>(
    `/api/candidate/applications/${appId}/commendations`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
}

export async function deleteCommendation(
  appId: string,
  commId: number,
): Promise<void> {
  await apiRequestVoid(
    `/api/candidate/applications/${appId}/commendations/${commId}`,
    { method: 'DELETE' },
  );
}

// ── Reusable file upload helpers ───────────────────────────────────────────

export interface RequestUploadUrlPayload {
  filename: string;
  contentType: string;
  modelType: 'staff_import' | 'candidate_application';
  modelId: string;
  documentPurpose:
    | 'bulk_import'
    | 'qualification_attachment'
    | 'certification_attachment'
    | 'commendation_attachment';
  size: number;
}

export interface RequestUploadUrlResponse {
  mediaId: string;
  uploadUrl: string;
  fileKey: string;
  expiresInMinutes: number;
}

export interface ConfirmUploadResponse {
  mediaId: string;
  fileKey: string;
  fileUrl: string;
}

export interface DownloadUrlResponse {
  mediaId: string;
  fileKey: string;
  downloadUrl: string;
  expiresInMinutes: number;
}

export interface StaffImportResult {
  created: number;
  updated: number;
  errors: Array<{
    row: number;
    staffId?: string;
    message: string;
  }>;
}

export function getStaffTemplateUrl(): string {
  return `${API_BASE}/staff_records_template.csv`;
}

export async function requestUploadUrl(
  payload: RequestUploadUrlPayload,
): Promise<RequestUploadUrlResponse> {
  return apiRequest<RequestUploadUrlResponse>('/api/files/upload-url', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export async function uploadFileToSignedUrl(
  uploadUrl: string,
  file: File,
): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type || 'application/octet-stream',
      'x-ms-blob-type': 'BlockBlob',
    },
    body: file,
  });

  if (!response.ok) {
    throw new Error('Upload to Azure Blob Storage failed');
  }
}

export async function confirmUpload(
  mediaId: string,
): Promise<ConfirmUploadResponse> {
  return apiRequest<ConfirmUploadResponse>(`/api/files/${mediaId}/confirm`, {
    method: 'POST',
  });
}

export async function getFileDownloadUrl(
  mediaId: string,
): Promise<DownloadUrlResponse> {
  return apiRequest<DownloadUrlResponse>(`/api/files/${mediaId}/download-url`);
}

export async function acceptUndertaking(version = 'v1'): Promise<{
  accepted: boolean;
  version: string;
  acceptedAt: string;
}> {
  return apiRequest('/api/candidates/undertaking/accept', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ version }),
  });
}

export async function importStaffRecords(
  mediaId: string,
): Promise<StaffImportResult> {
  return apiRequest<StaffImportResult>('/api/admin/staff-records/import', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ mediaId }),
  });
}

// ── Types ─────────────────────────────────────────────────────────────────

export interface EmploymentRecord {
  id: number;
  employer: string | null;
  positionHeld: string | null;
  roleDescription: string;
  workAchievement: string | null;
  otherAchievement: string | null;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
}

export type EmploymentBody = Omit<EmploymentRecord, 'id'>;

export interface MediaRecord {
  id: string;
  modelType: string;
  modelId: string;
  documentPurpose: string;
  name: string;
  fileKey: string;
  fileUrl: string;
  contentType: string;
  size: number;
}

export interface QualificationRecord {
  id: number;
  qualificationName: string | null;
  institution: string | null;
  acquisitionYear: number | null;
  mediaId: string | null;
  media?: MediaRecord | null;
}

export interface QualificationBody {
  qualificationName: string | null;
  institution: string | null;
  acquisitionYear: number | null;
  mediaId?: string | null;
}

export interface CertificationRecord {
  id: number;
  certificationName: string | null;
  institution: string | null;
  acquisitionYear: number | null;
  mediaId: string | null;
  media?: MediaRecord | null;
}

export interface CertificationBody {
  certificationName: string | null;
  institution: string | null;
  acquisitionYear: number | null;
  mediaId?: string | null;
}

export interface CommendationRecord {
  id: number;
  title: string | null;
  detail: string | null;
  comment: string | null;
  mediaId: string | null;
  media?: MediaRecord | null;
  mediaIds: string[];
  mediaFiles?: MediaRecord[];
}

export interface CommendationBody {
  title: string | null;
  detail: string | null;
  comment: string | null;
  mediaIds?: string[];
}

export interface WhyRecord {
  id: number;
  reason: string | null;
}

export interface ApplicationUpdate {
  surname?: string;
  firstName?: string;
  middleName?: string;
  gender?: string;
  gsmNumber?: string;
  emailAddr?: string;
  jobChoice?: number;
  yearsOfExperience?: number | null;
}

export interface ApplicationDetail {
  id: number;
  guid: string;
  jobRoleId: number;
  jobRoleGuid: string | null;
  jobRoleName: string | null;
  jobRoleCode: string | null;
  jobGradeLevel: string | null;
  sbu: string | null;
  sbuName: string | null;
  jobLocation: string | null;
  staffId: string;
  surname: string;
  firstName: string;
  middleName: string | null;
  gender: string | null;
  grade: string;
  designation: string | null;
  dateOfLastPromotion: string | null;
  nrd: string | null;
  gsmNumber: string | null;
  emailAddr: string | null;
  department: string | null;
  division: string | null;
  directorate: string | null;
  location: string | null;
  performanceTracking: string | null;
  verifiedQualificationWithDate: string | null;
  yearsOfExperience: number | null;
  jobChoice: number | null;
  /** 'assigned' — an admin directed the candidate to this role; no job choice, exempt from the cap. */
  source?: 'self' | 'assigned';
  submitted: boolean;
  submittedAt: string | null;
  canRecall?: boolean;
  createdAt: string;
  employment: EmploymentRecord[];
  why: WhyRecord | null;
  qualifications: QualificationRecord[];
  certifications: CertificationRecord[];
  commendations: CommendationRecord[];
}

// ── External applicant auth ────────────────────────────────────────────────────

export interface ExternalPortalUser {
  id: string;
  guid: string;
  applicantRefNo: string;
  emailAddress: string | null;
  surname: string | null;
  firstName: string | null;
  middleName: string | null;
  gender: 'M' | 'F' | null;
  gsmNumber: string | null;
  undertakingAcceptedAt: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface ExternalLoginResult {
  accessToken: string;
  refreshToken?: string;
  expiresIn: number;
  applicantRefNo: string;
  user: ExternalPortalUser;
}

export type CheckEmailStatus = 'found' | 'setup_pending' | 'not_found';

export async function checkEmail(
  email: string,
): Promise<{ status: CheckEmailStatus; otpRequired?: boolean }> {
  const res = await fetch(`${API_BASE}/api/auth/external/check-email`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

export async function externalLogin(
  email: string,
  password: string,
  otp?: string,
): Promise<ExternalLoginResult> {
  const res = await fetch(`${API_BASE}/api/auth/external/login`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password, otp }),
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

export async function verifySetupToken(
  token: string,
): Promise<{
  firstName: string | null;
  surname: string | null;
  email: string | null;
}> {
  const res = await fetch(`${API_BASE}/api/auth/external/verify-setup-token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

export async function setupPassword(
  token: string,
  newPassword: string,
): Promise<ExternalLoginResult> {
  const res = await fetch(`${API_BASE}/api/auth/external/setup-password`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token, newPassword }),
  });
  if (!res.ok) throw await toApiError(res);
  return res.json();
}

export async function getAssignedJobs(): Promise<
  (PublicJob & { isLocked: boolean })[]
> {
  try {
    const result = await apiRequest<(PublicJob & { isLocked: boolean })[]>(
      '/api/jobs/external/assigned',
    );
    return result ?? [];
  } catch {
    return [];
  }
}
